import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Plane, Lock, Download, LogOut, Users, ArrowLeft } from "lucide-react";
import { Input } from "@/components/ui/input";
import MediaManager from "@/components/MediaManager";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem("sposi_token") || "");
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [rsvps, setRsvps] = useState(null);

  const load = useCallback(async (t) => {
    try {
      const { data } = await axios.get(`${API}/admin/rsvps`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setRsvps(data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("sposi_token");
        setToken("");
        setRsvps(null);
      }
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/auth`, { pin });
      localStorage.setItem("sposi_token", data.token);
      setToken(data.token);
      setPin("");
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(typeof d === "string" ? d : "Errore di accesso. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  const downloadCsv = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/rsvps.csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "conferme-sposi.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError("Download non riuscito. Riprova.");
    }
  };

  const logout = () => {
    localStorage.removeItem("sposi_token");
    setToken("");
    setRsvps(null);
  };

  const attending = (rsvps || []).filter((r) => r.attending);
  const totalGuests = attending.reduce((s, r) => s + (r.guests_count || 1), 0);

  return (
    <div className="min-h-screen bg-panna text-[#2D2A26] antialiased px-6 sm:px-12 py-10" data-testid="admin-page">
      <div className="mx-auto max-w-5xl">
        <div className="flex items-center justify-between mb-12">
          <a href="/" className="flex items-center gap-2 group" data-testid="admin-home-link">
            <Plane className="w-4 h-4 text-terracotta transition-transform duration-500 group-hover:rotate-45" />
            <span className="font-serif text-lg tracking-tight">F &amp; A</span>
          </a>
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-clay">Area Sposi</span>
        </div>

        {!token ? (
          <motion.form
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            onSubmit={submit}
            className="mx-auto max-w-sm rounded-2xl border border-sand bg-cream p-8 sm:p-10 text-center"
            data-testid="pin-form"
          >
            <div className="w-12 h-12 mx-auto rounded-full bg-panna border border-sand flex items-center justify-center mb-6">
              <Lock className="w-5 h-5 text-terracotta" />
            </div>
            <h1 className="font-serif text-2xl mb-2">Area riservata</h1>
            <p className="text-sm text-[#5C564E] mb-8">Inserisci il PIN per vedere le conferme di presenza.</p>
            <Input
              type="password"
              inputMode="numeric"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder="PIN"
              className="bg-panna border-sand text-center font-mono tracking-[0.5em] text-lg"
              data-testid="pin-input"
            />
            {error && (
              <p className="mt-4 text-sm text-red-700" data-testid="pin-error">{error}</p>
            )}
            <button
              type="submit"
              disabled={loading || !pin.trim()}
              className="mt-6 w-full rounded-full bg-terracotta text-panna font-mono text-[11px] uppercase tracking-[0.25em] px-6 py-4 transition-all duration-300 hover:bg-[#b25c42] disabled:opacity-60"
              data-testid="pin-submit-button"
            >
              {loading ? "Verifica..." : "Accedi"}
            </button>
          </motion.form>
        ) : (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <div>
                <h1 className="font-serif text-3xl sm:text-4xl" data-testid="admin-title">Conferme di presenza</h1>
                <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.25em] text-clay" data-testid="admin-stats">
                  {attending.length} risposte · {totalGuests} invitati confermati
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={downloadCsv}
                  className="inline-flex items-center gap-2 rounded-full bg-charcoal text-panna font-mono text-[11px] uppercase tracking-[0.2em] px-5 py-3 transition-colors duration-300 hover:bg-terracotta"
                  data-testid="csv-download-button"
                >
                  <Download className="w-4 h-4" /> Scarica CSV
                </button>
                <button
                  onClick={logout}
                  className="inline-flex items-center gap-2 rounded-full border border-sand bg-panna font-mono text-[11px] uppercase tracking-[0.2em] px-5 py-3 transition-colors duration-300 hover:border-terracotta hover:text-terracotta"
                  data-testid="logout-button"
                >
                  <LogOut className="w-4 h-4" /> Esci
                </button>
              </div>
            </div>

            {rsvps === null ? (
              <p className="text-sm text-clay font-mono" data-testid="admin-loading">Caricamento...</p>
            ) : rsvps.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-sand bg-cream/60 p-12 text-center" data-testid="admin-empty">
                <Users className="w-6 h-6 mx-auto text-clay mb-4" />
                <p className="text-sm text-[#5C564E]">Nessuna conferma ricevuta finora.</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="rsvp-list">
                {rsvps.map((r) => (
                  <div
                    key={r.id}
                    className="rounded-2xl border border-sand bg-cream p-6"
                    data-testid={`rsvp-row-${r.id}`}
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                      <span className="font-serif text-lg">
                        {(r.guest_names && r.guest_names.filter(Boolean).join(", ")) || r.name}
                      </span>
                      <span className={`font-mono text-[10px] uppercase tracking-[0.2em] ${r.attending ? "text-sage" : "text-red-700"}`}>
                        {r.attending ? "Presente" : "Assente"}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-2 text-sm text-[#5C564E]">
                      <span>Invitati: <strong className="text-[#2D2A26]">{r.guests_count}</strong></span>
                      <span>Menù: <strong className="text-[#2D2A26]">{r.menu_choice}</strong></span>
                      <span>Bimbi: <strong className="text-[#2D2A26]">{r.kids_info || "—"}</strong></span>
                      <span className="font-mono text-xs self-center">{String(r.created_at).slice(0, 10)}</span>
                    </div>
                    {(r.dietary_requirements || r.song_request || r.notes) && (
                      <div className="mt-3 pt-3 border-t border-sand/70 text-sm text-[#5C564E] space-y-1">
                        {r.dietary_requirements && <p>Intolleranze: {r.dietary_requirements}</p>}
                        {r.song_request && <p>Canzone: {r.song_request}</p>}
                        {r.notes && <p>Note: {r.notes}</p>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="mt-14 pt-10 border-t border-sand/70">
              <h2 className="font-serif text-2xl mb-2" data-testid="media-section-title">Foto &amp; Video della Gallery</h2>
              <p className="text-sm text-[#5C564E] mb-6">
                I file caricati qui vengono pubblicati automaticamente nel Capitolo 04 — Gallery del sito.
              </p>
              <MediaManager token={token} />
            </div>

            <a
              href="/"
              className="mt-10 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-clay hover:text-terracotta transition-colors"
              data-testid="admin-back-link"
            >
              <ArrowLeft className="w-4 h-4" /> Torna al sito
            </a>
          </motion.div>
        )}
      </div>
    </div>
  );
}
